import axios from 'axios';
import { PaymentGateway } from '../applications/types/applications.types';
import { CURSEDUCA_PLANS_REFERENCES } from '../../pages/Checkout/components/Form';

export type TokenizeCreditCardDto = {
	hasAntifraud: boolean;
	type: 'card';
	// Gateway resolvido pela plataforma (via /applications/params). Decide onde
	// o cartão é tokenizado. Ausente => heurística legada (Vindi por path / Pagar.me).
	gateway?: PaymentGateway;
	card: {
		number: string;
		holder_name: string;
		exp_month: number;
		exp_year: number;
		cvv: string;
	};
};

export type TokenizeCreditCardResponse = {
	id: string;
	type: 'card';
	created_at: string;
	expires_at: string;
	card: {
		first_six_digits: string;
		last_four_digits: string;
		holder_name: string;
		exp_month: number;
		exp_year: number;
		brand: string;
	};
};

export const tokenize = async (data: TokenizeCreditCardDto) => {
	const today = new Date();
	const expiryDate = new Date();
	expiryDate.setFullYear(
		2000 + Number(data.card.exp_year),
		Number(data.card.exp_month),
		1
	);

	if (expiryDate < today) {
		throw new Error('Cartão de informado está expirado.');
	}

	// 1) Gateway explícito da plataforma (fonte da verdade = backend). O token
	//    precisa ser emitido no mesmo gateway que o backend vai cobrar.
	if (data.gateway === 'MALGA') {
		return malgaTokenize(data);
	}
	if (data.gateway === 'VINDI') {
		return vindiTokenize(data);
	}
	if (data.gateway === 'PAGARME') {
		return pagarmeTokenize(data);
	}

	// 2) Retro-compat: sem gateway explícito, mantém a heurística legada
	//    (Vindi por path do plano; caso contrário Pagar.me).
	const isVindi = !!Object.keys(CURSEDUCA_PLANS_REFERENCES).find((r) => {
		return location.pathname.includes(r);
	});

	if (isVindi) {
		return vindiTokenize(data);
	}

	return pagarmeTokenize(data);
};

const pagarmeTokenize = async ({
	hasAntifraud,
	...data
}: TokenizeCreditCardDto) => {
	const publicKey = hasAntifraud
		? process.env.REACT_APP_PAGARME_PUBLIC_KEY
		: process.env.REACT_APP_PAGARME_PUBLIC_KEY_WITHOUT_ANTIFRAUD;

	const response = await axios.post<TokenizeCreditCardResponse>(
		`https://api.pagar.me/core/v5/tokens?appId=${publicKey}`,
		data
	);

	return response.data;
};

const vindiTokenize = async ({ card }: TokenizeCreditCardDto) => {
	const r = await fetch(
		'https://app.vindi.com.br/api/v1/public/payment_profiles',
		{
			method: 'POST',
			body: JSON.stringify({
				holder_name: card.holder_name,
				card_expiration: `${card.exp_month}/${card.exp_year}`,
				allow_as_fallback: true,
				card_number: card.number,
				card_cvv: card.cvv,
				payment_method_code: 'credit_card'
			}),
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Basic ${btoa(
					process.env.REACT_APP_VINDI_API_KEY as string
				)}`
			}
		}
	);

	const j = await r.json();

	return {
		card: {
			first_six_digits: card.number.slice(0, 6),
			last_four_digits: card.number.slice(-4),
			holder_name: card.holder_name,
			exp_month: card.exp_month,
			exp_year: card.exp_year,
			brand: ''
		},
		id: j.payment_profile.gateway_token
	};
};

// Tokenização client-side da Malga: POST /v1/tokens → { tokenId }.
// ⚠️ SEGURANÇA: em sandbox usamos X-Client-Id + X-Api-Key vindos do env. Antes do
// go-live, confirmar com a Malga que a key exposta no browser é tokenization-scoped
// (publishable), NÃO a secret de servidor. Ver docs/WAID-PAY-V2 (front tokenizer).
const malgaTokenize = async ({
	card
}: TokenizeCreditCardDto): Promise<TokenizeCreditCardResponse> => {
	const baseUrl =
		process.env.REACT_APP_MALGA_BASE_URL || 'https://api.malga.io/v1';

	// Malga exige validade no formato MM/YYYY. exp_year pode vir com 2 dígitos.
	const expMonth = String(card.exp_month).padStart(2, '0');
	const expYear =
		String(card.exp_year).length === 2
			? `20${card.exp_year}`
			: String(card.exp_year);

	const { data } = await axios.post<{ tokenId: string }>(
		`${baseUrl}/tokens`,
		{
			cardNumber: card.number,
			cardHolderName: card.holder_name,
			cardCvv: card.cvv,
			cardExpirationDate: `${expMonth}/${expYear}`
		},
		{
			headers: {
				'Content-Type': 'application/json',
				'X-Client-Id': process.env.REACT_APP_MALGA_CLIENT_ID as string,
				'X-Api-Key': process.env.REACT_APP_MALGA_API_KEY as string
			}
		}
	);

	return {
		id: data.tokenId,
		type: 'card',
		created_at: '',
		expires_at: '',
		card: {
			first_six_digits: card.number.slice(0, 6),
			last_four_digits: card.number.slice(-4),
			holder_name: card.holder_name,
			exp_month: card.exp_month,
			exp_year: card.exp_year,
			brand: ''
		}
	};
};
